import { useState } from "react";

// Components
import CustomIcon from "components/CustomIcon";

// MUI
import { Menu, MenuItem, IconButton, Typography, useTheme } from "@mui/material";

// Store
import { clearChart } from "store/slice/charts";

// Others
import icons from "enums/icons";
import { urlPaths } from "enums/urlPaths";
import { getInsightsList } from "pages/ProjectDashboard";

// Hooks
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

interface SettingsDropDownProps {
    projectId: string | undefined;
    insight: ReturnType<typeof getInsightsList>[number];
}

const SettingsDropDown = ({ projectId, insight }: SettingsDropDownProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const hasRelatedDomain = Boolean(insight.meta.peers);
    const hasRelatedSLT = Boolean(insight.meta.sltCount);
    const hasRelatedVCP = Boolean(insight.meta.vcp);
    const hasCompletedInsight = hasRelatedDomain && hasRelatedSLT && hasRelatedVCP;

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleGoToDomain = () => {
        navigate(`${urlPaths.Domain}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`, {
            state: { from: `${urlPaths.ProjectDashboard}/${projectId}` },
        });
    };

    const handleGoToSetTeam = () => {
        navigate(`${urlPaths.Team}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`, {
            state: { from: `${urlPaths.ProjectDashboard}/${projectId}` },
        });
    };

    const handleGoToVcp = () => {
        navigate(`${urlPaths.Vcp}/${insight.meta.companyId}?projectId=${projectId}&insightId=${insight.id}`, {
            state: { from: `${urlPaths.ProjectDashboard}/${projectId}` },
        });
    };

    const handleViewInsight = () => {
        dispatch(clearChart());
        navigate(
            `${urlPaths.Projection}/${insight.meta.companyId}/balance?projectId=${projectId}&insightId=${insight.id}`
        );
    };

    const dropDownOptions = [
        { label: "Edit Domain", icon: icons.bubbles, onClick: handleGoToDomain },
        ...(hasRelatedDomain ? [{ label: "Edit Team", icon: icons.teamStructure, onClick: handleGoToSetTeam }] : []),
        ...(hasRelatedSLT ? [{ label: "Edit VCP", icon: icons.book, onClick: handleGoToVcp }] : []),
        ...(hasCompletedInsight ? [{ label: "View Insight", icon: icons.sparkles, onClick: handleViewInsight }] : []),
    ];

    return (
        <>
            <IconButton
                aria-haspopup="true"
                onClick={handleClick}
                id="insight-settings-button"
                aria-expanded={open ? "true" : undefined}
                aria-controls={open ? "insight-settings-menu" : undefined}
            >
                <CustomIcon icon={icons.horizontalDots} sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <Menu
                id="insight-settings-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        width: 165,
                    },
                }}
            >
                {dropDownOptions.map((option) => (
                    <MenuItem key={option.label} disabled={!projectId} onClick={option.onClick} sx={{ p: 1, pl: 2 }}>
                        <CustomIcon
                            icon={option.icon}
                            sx={{ fontSize: 13, mr: 1, color: theme.palette.text.primary }}
                        />
                        <Typography sx={{ fontSize: 16 }}>{option.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default SettingsDropDown;
